import React, { useEffect, useState } from 'react';
import { Link } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';
import { add, isBefore } from 'date-fns';

import { InputCountry } from '@/components/InputCountry/InputCountry';
import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import IconHelp from '@/icons/Help.svg';
import { useOrganizationWorkflows } from '@/models/organization';
import { useCreateDraftGuestRequest } from '@/models/request';
import { Store, useStores } from '@/models/store';
import { ExternalStoreInfo } from '@/routes/Brand/Requests/New/components/ExternalStoreInfo';
import PoweredBy from '@/routes/Brand/Requests/New/components/PoweredBy';
import { useCurrentOrganization, useGeoIpCountry } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';
import {
  REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY,
  useRequestInProgress,
} from '@/utils/requestInProgress';
import useDebouncedState from '@/utils/useDebouncedState';
import useViewPort from '@/utils/useViewport';

import './Welcome.css';

const { block, element } = createBEMClasses('client-new-welcome');

const requestTypes = ['external-store', 'internal-store', 'web-client'] as const;

type RequestType = (typeof requestTypes)[number];

const Welcome = () => {
  const { t } = useLingui();
  const [organization] = useCurrentOrganization();
  const navigate = useNavigate();

  const codeInUrl = new URLSearchParams(window.location.search).get('code');

  const geoIpCountry = useGeoIpCountry();
  const [country, setCountry] = useState<string | undefined>(geoIpCountry);
  const [type, setType] = useState<RequestType>(codeInUrl ? 'external-store' : 'web-client');
  const [internalStoreId, setInternalStoreId] = useState<string>();
  const [storeAccessCode, debouncedStoreAccessCode, setStoreAccessCode] = useDebouncedState<string>(
    codeInUrl ?? '',
    500
  );

  const { data: workflows } = useOrganizationWorkflows(organization?.slug);

  const clientCreationConfig = workflows
    ?.find((workflow) => workflow.type === 'client')
    ?.nodes.find((node) => 'step' in node && node.step === 'creation')?.config;

  const hasClientWorkflow = !!workflows?.find((workflow) => workflow.type === 'client');
  const hasNonEmbeddedStoreWorkflow = !!workflows?.find(
    (workflow) =>
      (workflow.type === 'client_via_store' || workflow.type === 'store') &&
      workflow.config.availableToNonEmbeddedStore
  );

  const { data: { stores: nonEmbeddedInternalStores } = { stores: [] } } = useStores(
    {
      internal: true,
      embedded: false,
      limit: 100,
    },
    {
      enabled: hasNonEmbeddedStoreWorkflow,
    }
  );

  const internalStore = nonEmbeddedInternalStores.find((store) => store.id === internalStoreId);

  const { data: { stores: [externalStore] } = { stores: [] }, isLoading: isExternalStoresLoading } =
    useStores(
      {
        accessCode: debouncedStoreAccessCode,
        internal: false,
        embedded: false,
        limit: 1,
      },
      {
        enabled: debouncedStoreAccessCode.length === 12,
      }
    );

  const { mutateAsync: createDraftGuestRequest, isPending: isCreateDraftGuestRequestPending } =
    useCreateDraftGuestRequest();
  const { requestInProgress, setRequestInProgress } = useRequestInProgress();

  useEffect(() => {
    if (requestInProgress) {
      const storageRequestDatePlus24h = add(requestInProgress.date, { hours: 24 });
      const isStorageRequestDateMoreThan24hAgo = isBefore(storageRequestDatePlus24h, new Date());

      if (isStorageRequestDateMoreThan24hAgo) {
        window.localStorage.removeItem(REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY);
      } else {
        const tokenQueryParam = requestInProgress.clientToken
          ? `clientToken=${requestInProgress.clientToken}`
          : `storeToken=${requestInProgress.storeToken}`;

        navigate(
          `/brand/${organization?.slug}/requests/new?id=${requestInProgress.id}&${tokenQueryParam}`,
          { replace: true }
        );

        setRequestInProgress({
          ...requestInProgress,
          date: new Date(),
        });
      }
    }
    // Adding requestInProgress to the dependencies array would cause an infinite loop
  }, [navigate, organization?.slug, setRequestInProgress]);

  const handleContinue = async () => {
    if (disableContinue) {
      return;
    }

    if (type === 'web-client') {
      const response = await createDraftGuestRequest({ country, createArticle: true });

      if ('clientToken' in response) {
        setRequestInProgress({
          id: response.id,
          clientToken: response.clientToken,
          date: new Date(),
        });

        navigate(`?id=${response.id}&clientToken=${response.clientToken}`, { replace: true });
      }
    } else if (type === 'internal-store') {
      const response = await createDraftGuestRequest({
        storeId: internalStoreId,
        createArticle: true,
      });

      if ('storeToken' in response) {
        setRequestInProgress({
          id: response.id,
          storeToken: response.storeToken,
          date: new Date(),
        });

        navigate(`?id=${response.id}&storeToken=${response.storeToken}`, { replace: true });
      }
    } else if (type === 'external-store') {
      const response = await createDraftGuestRequest({
        storeId: externalStore?.id,
        accessCode: debouncedStoreAccessCode,
        createArticle: true,
      });

      if ('storeToken' in response) {
        setRequestInProgress({
          id: response.id,
          storeToken: response.storeToken,
          date: new Date(),
        });

        navigate(`?id=${response.id}&storeToken=${response.storeToken}`, { replace: true });
      }
    }
  };

  const askForCountry = type === 'web-client' && !!clientCreationConfig?.requirePrice;

  const disableContinue =
    (type === 'web-client' && askForCountry && !country) ||
    (type === 'internal-store' && !internalStore) ||
    (type === 'external-store' && !externalStore);

  const requestorTypeOptions = [
    hasClientWorkflow
      ? {
          id: 'web-client',
          text: t({
            id: 'client.new.welcome.type.web_client',
            message: 'Individual Customer',
          }),
        }
      : null,
    hasNonEmbeddedStoreWorkflow
      ? {
          id: 'external-store',
          text: t({
            id: 'client.new.welcome.type.client_via_external_store',
            message: 'Retailer / Wholesaler',
          }),
        }
      : null,
    hasNonEmbeddedStoreWorkflow && nonEmbeddedInternalStores.length > 0
      ? {
          id: 'internal-store',
          text: t({
            id: 'client.new.welcome.type.client_via_internal_store',
            message: 'Outlet / Affiliated / Proper store',
          }),
        }
      : null,
  ].filter(Boolean);

  const { isMobile } = useViewPort();

  return (
    <div className={block()}>
      <Stack className={element('header')}>
        <Stack
          className={element('header__image')}
          style={{
            backgroundImage: organization?.config.appStyle.diagnosticForm?.welcome?.header?.image
              ? `url(${organization.config.appStyle.diagnosticForm.welcome.header.image})`
              : "linear-gradient(180deg, rgb(0 0 0 / 0%) 0%, rgb(0 0 0 / 15%) 50%, rgb(0 0 0 / 50%) 100%), url('/new-request-welcome-header.webp')",
            backgroundPosition: organization?.config.appStyle.diagnosticForm?.welcome?.header?.image
              ? 'center'
              : 'top 40% right 50%',
          }}
        />
        <Stack className={element('header__text')}>
          <Title
            weight="bold"
            size={isMobile ? '200' : '50'}
            as="h1"
            color="neutral"
            className={element('header__text__title')}
          >
            {organization?.name ? (
              <Trans id="client.new.welcome.title-with-organization">
                {organization.name}'s care and repair service
              </Trans>
            ) : (
              <Trans id="client.new.welcome.title-without-organization">
                Care and repair service
              </Trans>
            )}
          </Title>
          {isMobile ? (
            <Paragraph size="50" color="neutral">
              <Trans id="client.new.welcome.subtitle">
                Extend the Life of Your Loved Apparels & Accessories.
              </Trans>
            </Paragraph>
          ) : (
            <Title size="200" weight="medium" color="neutral">
              <Trans id="client.new.welcome.subtitle">
                Extend the Life of Your Loved Apparels & Accessories.
              </Trans>
            </Title>
          )}
        </Stack>
      </Stack>

      <main>
        <Stack className={element('main')}>
          <Stack gap="1.5rem">
            {requestorTypeOptions.length > 1 && (
              <InputSelect
                variant="select"
                label={t({
                  id: 'client.new.welcome.type.label',
                  message: 'Tell us who you are',
                })}
                placeholder={t({
                  id: 'client.new.welcome.type.placeholder',
                  message: 'Select a requestor type',
                })}
                isSearchable={false}
                options={requestorTypeOptions}
                value={requestorTypeOptions.filter(
                  (requestorTypeOption) => type === requestorTypeOption!.id
                )}
                getOptionValue={(requestorTypeOption) => requestorTypeOption!.id}
                getOptionLabel={(requestorTypeOption) => requestorTypeOption!.text}
                onChange={(requestorTypeOption) => {
                  // @ts-ignore requestTypes is a const array but we want to check if key is in it
                  if (requestorTypeOption && requestTypes.includes(requestorTypeOption.id)) {
                    setType(requestorTypeOption.id as RequestType);
                  }

                  if (requestorTypeOption?.id === 'web-client') {
                    setStoreAccessCode('');
                  }
                }}
                styleVariant="brand"
                size="large"
                style={{ alignSelf: 'stretch' }}
              />
            )}

            {askForCountry && (
              <InputCountry
                label={t({
                  id: 'client.new.welcome.country.label',
                  message: 'Please tell us your country to adapt the currency',
                })}
                placeholder={t({
                  id: 'client.new.welcome.country.placeholder',
                  message: 'Select a country',
                })}
                value={country}
                onChange={(country) => {
                  if (country) {
                    setCountry(country);
                  }
                }}
                styleVariant="brand"
                size="large"
                style={{ alignSelf: 'stretch' }}
              />
            )}

            {type === 'external-store' && (
              <Stack gap="1rem">
                <ExternalStoreAccessCodeInput
                  storeAccessCode={storeAccessCode}
                  debouncedStoreAccessCode={debouncedStoreAccessCode}
                  setStoreAccessCode={setStoreAccessCode}
                  isExternalStoresLoading={isExternalStoresLoading}
                  store={externalStore}
                />
                {!!externalStore && <ExternalStoreInfo store={externalStore} />}
              </Stack>
            )}

            {type === 'internal-store' && (
              <InputSelect
                variant="select"
                label={t({
                  id: 'client.new.welcome.internal-store.label',
                  message: 'Select your store',
                })}
                placeholder={t({
                  id: 'client.new.welcome.internal-store.placeholder',
                  message: 'Select your store',
                })}
                isSearchable={false}
                options={nonEmbeddedInternalStores}
                value={nonEmbeddedInternalStores.filter(
                  (internalStoreOption) => internalStoreId === internalStoreOption.id
                )}
                getOptionValue={(internalStoreOption) => internalStoreOption.id}
                getOptionLabel={(internalStoreOption) => internalStoreOption.name}
                onChange={(internalStoreOption) => {
                  if (internalStoreOption) {
                    setInternalStoreId(internalStoreOption.id);
                  }
                }}
                hintText={
                  <ContactUs>
                    <Trans id="client.new.welcome.contact-us.cannot-find">
                      Don't find your store?
                    </Trans>
                  </ContactUs>
                }
                styleVariant="brand"
                size="large"
                style={{ alignSelf: 'stretch' }}
              />
            )}
          </Stack>

          <div className={element('stepper')}>
            <div
              className={element('stepper__image')}
              style={{
                backgroundImage: organization?.config.appStyle.diagnosticForm?.welcome
                  ?.howDoesItWork?.image
                  ? `url(${organization?.config.appStyle.diagnosticForm.welcome.howDoesItWork.image})`
                  : 'url(/new-request-welcome.jpeg)',
              }}
            />
            <Stack gap="1rem">
              <Title size="300" weight="bold" as="h2">
                <Trans id="client.new.welcome.stepper.title">How does it work?</Trans>
              </Title>
              <ul>
                <li className={element('stepper__item')}>
                  <InlineText
                    weight="medium"
                    color="neutral"
                    className={element('stepper__item__index')}
                  >
                    1
                  </InlineText>
                  <div>
                    <Paragraph size="50" weight="medium">
                      <Trans id="client.new.welcome.stepper.1.title">Get a free estimate</Trans>
                    </Paragraph>
                    <Paragraph color="secondary">
                      <Trans id="client.new.welcome.stepper.1.detail">
                        Share the details of your item(s) needing attention. It only takes a moment
                        to get a free estimate.
                      </Trans>
                    </Paragraph>
                  </div>
                </li>
                <li className={element('stepper__item')}>
                  <InlineText
                    weight="medium"
                    color="neutral"
                    className={element('stepper__item__index')}
                  >
                    2
                  </InlineText>
                  <div>
                    <Paragraph size="50" weight="medium">
                      <Trans id="client.new.welcome.stepper.2.title">Send your item(s)</Trans>
                    </Paragraph>
                    <Paragraph color="secondary">
                      <Trans id="client.new.welcome.stepper.2.detail">
                        Ship your item(s) to our trusted expert partner.
                      </Trans>
                    </Paragraph>
                  </div>
                </li>
                <li className={element('stepper__item')}>
                  <InlineText
                    weight="medium"
                    color="neutral"
                    className={element('stepper__item__index')}
                  >
                    3
                  </InlineText>
                  <div>
                    <Paragraph size="50" weight="medium">
                      <Trans id="client.new.welcome.stepper.3.title">Sit back and relax</Trans>
                    </Paragraph>
                    <Paragraph color="secondary">
                      <Trans id="client.new.welcome.stepper.3.detail">
                        Our experts will handle the rest. You'll be notified as soon as your item is
                        ready.
                      </Trans>
                    </Paragraph>
                  </div>
                </li>
                <li className={element('stepper__item')}>
                  <InlineText
                    weight="medium"
                    color="neutral"
                    className={element('stepper__item__index')}
                  >
                    4
                  </InlineText>
                  <div>
                    <Paragraph size="50" weight="medium">
                      <Trans id="client.new.welcome.stepper.4.title">
                        Receive your revamped item
                      </Trans>
                    </Paragraph>
                    <Paragraph color="secondary">
                      <Trans id="client.new.welcome.stepper.4.detail">
                        You'll get your item back shortly, ready to wear!
                      </Trans>
                    </Paragraph>
                  </div>
                </li>
              </ul>
            </Stack>
          </div>

          <PoweredBy />
        </Stack>
      </main>
      <footer>
        <Stack>
          <Button
            size="large"
            variant="brand"
            style={{ flex: 1 }}
            disabled={disableContinue}
            isLoading={isCreateDraftGuestRequestPending}
            onPress={handleContinue}
          >
            <Trans id="client.new.welcome.continue">Continue</Trans>
            <IconArrow right />
          </Button>
        </Stack>
      </footer>
    </div>
  );
};

const ExternalStoreAccessCodeInput = ({
  storeAccessCode,
  debouncedStoreAccessCode,
  setStoreAccessCode,
  isExternalStoresLoading,
  store,
}: {
  storeAccessCode: string;
  debouncedStoreAccessCode: string;
  setStoreAccessCode: (storeAccessCode: string) => void;
  isExternalStoresLoading: boolean;
  store?: Store | null;
}) => {
  const { t } = useLingui();

  let error;

  if (debouncedStoreAccessCode.length && debouncedStoreAccessCode.length !== 12) {
    error = t({
      id: 'client.new.welcome.external-store-code.invalid-length',
      message: 'Code must be 12 characters long',
    });
  } else if (debouncedStoreAccessCode.length === 12 && !isExternalStoresLoading && !store) {
    error = t({
      id: 'client.new.welcome.external-store-code.invalid',
      message: 'Invalid code',
    });
  }

  return (
    <InputText
      size="large"
      label={t({
        id: 'client.new.welcome.external-store-code.label',
        message: 'After-Sales code',
      })}
      aria-label={t({
        id: 'client.new.welcome.external-store-code.label',
        message: 'After-Sales code',
      })}
      placeholder={t({
        id: 'client.new.welcome.external-store-code.label.placeholder',
        message: 'Type your 12 characters code',
      })}
      value={storeAccessCode}
      onChange={(value) => setStoreAccessCode(value)}
      isLoading={isExternalStoresLoading}
      error={error}
      hintText={
        store ? undefined : (
          <ContactUs>
            <Trans id="client.new.welcome.contact-us.forgot-code">Forgot your code?</Trans>
          </ContactUs>
        )
      }
      showMessageIcon={false}
    />
  );
};

const ContactUs = ({ children }: { children: React.ReactNode }) => {
  const [currentOrganization] = useCurrentOrganization();

  if (!currentOrganization?.config.afterSalesServiceEmail) {
    return null;
  }

  return (
    <Stack row alignItems="center">
      <IconHelp className="text-secondary" style={{ marginRight: '0.25rem' }} />
      <Paragraph>
        <span className="text-secondary">{children}</span>{' '}
        <Trans id="client.new.welcome.contact-us.link">
          Contact us at{' '}
          <Link
            className="color-tertiary-700"
            href={`mailto:${currentOrganization?.config.afterSalesServiceEmail}`}
            target="_blank"
          >
            {currentOrganization?.config.afterSalesServiceEmail}
          </Link>
        </Trans>
      </Paragraph>
    </Stack>
  );
};

export default Welcome;
